import { useEffect, useState } from "react";
import { Button } from "@/shared/components/button";
import { AppModal } from "@/shared/components/dialog";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { ClinicianPicker } from "@/features/admin/clinicians/components/ClinicianPicker";
import { assignReviewItemApi } from "@/portals/admin/api/adminReviewQueueApi";
import type { ClinicianListItem } from "@/portals/clinician/api/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminKeys, cliniciansQuery, invalidateReviewQueue } from "@/portals/admin/api/adminQueries";

export const ASSIGN_MESSAGES = {
  assigned: "Item assigned.",
  notAssignable: "This item was already claimed or assigned.",
  notEligible: "This clinician can't review this item.",
  clinicianGone: "That clinician is no longer available.",
  itemGone: "This item isn't available.",
  reassigned: "Item reassigned.",
  alreadyAssigned: "This item is already with that clinician.",
};

const BRAND = "!h-auto !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";
const OUTLINE = "!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-medium !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]";

interface AssignClinicianModalProps {
  /** The queued item to assign; null keeps the dialog closed. */
  itemId: string | null;
  onClose: () => void;
  /** Assigned — the item has left the queue. */
  onAssigned: (id: string) => void;
  /** Claimed/assigned by someone else meanwhile, or gone — the item has left the queue. */
  onItemGone: (id: string) => void;
  /** Reassigning a held item: its current clinician (left out of the list). */
  currentClinicianId?: string | null;
}

/** "Assign to a clinician" — shared by the queue list and the item's view screen. */
export function AssignClinicianModal({ itemId, onClose, onAssigned, onItemGone, currentClinicianId }: AssignClinicianModalProps) {
  const reassigning = Boolean(currentClinicianId);
  const [choice, setChoice] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const client = useQueryClient();
  // The queue, its summary, item views and assigned items all reflect the change.
  const refreshQueue = () => void invalidateReviewQueue(client);

  // A fresh pick every time the dialog opens.
  useEffect(() => setChoice(undefined), [itemId]);

  // Only fetched while the dialog is open; reopening it shows the cached list instantly.
  const clinicianList = useQuery({ ...cliniciansQuery(), enabled: itemId !== null });
  const clinicians: ClinicianListItem[] = clinicianList.data ?? [];

  const close = () => {
    if (!saving) onClose();
  };

  // Toasts carry no medicine, patient or clinician names — only the fixed messages.
  const assign = async () => {
    if (!itemId || !choice) return;
    setSaving(true);
    const { data, error } = await assignReviewItemApi(itemId, choice);
    setSaving(false);
    if (data) {
      toastSuccess(reassigning ? ASSIGN_MESSAGES.reassigned : ASSIGN_MESSAGES.assigned);
      refreshQueue();
      onAssigned(itemId);
      return;
    }
    switch (error?.code) {
      case "ITEM_NOT_ASSIGNABLE":
        toastError(ASSIGN_MESSAGES.notAssignable);
        refreshQueue();
        onItemGone(itemId);
        break;
      case "REVIEW_ITEM_NOT_FOUND":
        toastError(ASSIGN_MESSAGES.itemGone);
        refreshQueue();
        onItemGone(itemId);
        break;
      case "ALREADY_ASSIGNED":
        toastError(ASSIGN_MESSAGES.alreadyAssigned);
        setChoice(undefined);
        break;
      case "CLINICIAN_NOT_ELIGIBLE":
        toastError(ASSIGN_MESSAGES.notEligible);
        setChoice(undefined);
        break;
      case "CLINICIAN_NOT_FOUND":
        toastError(ASSIGN_MESSAGES.clinicianGone);
        setChoice(undefined);
        void client.invalidateQueries({ queryKey: adminKeys.clinicians });
        break;
      default:
        toastError(error?.message ?? "Unable to assign the item.");
    }
  };

  return (
    <AppModal
      open={itemId !== null}
      onOpenChange={(o) => { if (!o) close(); }}
      title={<span className="text-[#0A2947]">{reassigning ? "Reassign to another clinician" : "Assign to a clinician"}</span>}
      description={
        <span className="text-[#4B5563]">The clinician you choose will be notified and can review it right away.</span>
      }
      width={480}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={saving} onClick={close} className={OUTLINE}>Cancel</Button>
          <Button disabled={!choice} loading={saving} onClick={() => void assign()} className={BRAND}>{reassigning ? "Reassign item" : "Assign item"}</Button>
        </div>
      }
    >
      <ClinicianPicker clinicians={clinicians} value={choice} onChange={setChoice} excludeUserId={currentClinicianId ?? undefined} />
    </AppModal>
  );
}
